/* eslint @typescript-eslint/no-explicit-any: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */

import AnswerSerializer from "./serializers/AnswerSerializer";
import { apiSlice } from "./apiSlice";
import { Answer } from "../../app/types/Answer";

const answerSerializer = new AnswerSerializer();

const answerApi = apiSlice.injectEndpoints({
    endpoints: (build) => ({
        getAnswers: build.query<Answer[], number>({
            query: (questionId) => ({ url: `/NI-api/question/${questionId}/answers` }),
            transformResponse: (response: any[]) => answerSerializer.deserializeList(response),
            providesTags: (result = [], error, arg) => [
                "Answer",
                ...(result.map(({ id }) => ({ type: "Answer", id })) as { type: "Answer"; id: number }[]),
            ],
        }),
        getAnswer: build.query<Answer, number>({
            query: (id) => ({ url: `/NI-api/answer/${id}/` }),
            transformResponse: (response) => answerSerializer.deserialize(response),
            providesTags: (result, error, arg) => [{ type: "Answer", id: arg }],
        }),
        createAnswer: build.mutation<number, Answer>({
            query: (answer) => ({
                url: `/NI-api/question/${answer.questionId}/answers`,
                method: "POST",
                data: answerSerializer.serialize(answer),
            }),
            transformResponse: (response: any) => response.id,
            invalidatesTags: (result, error, arg) => ["Answer", { type: "Question", id: arg.questionId }],
        }),
        updateAnswer: build.mutation<void, Answer>({
            query: (answer) => ({
                url: `/NI-api/answer/${answer.id}/`,
                method: "PUT",
                data: answerSerializer.serialize(answer),
            }),
            invalidatesTags: (result, error, arg) => [{ type: "Answer", id: arg.id }],
        }),
        acceptAnswer: build.mutation<void, Answer>({
            query: (answer) => ({
                url: `/NI-api/question/${answer.questionId}/`,
                method: "PATCH",
                data: { accepted_answer: answer.accepted ? answer.id : null },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "Question", id: arg.questionId },
                { type: "Answer", id: arg.id },
            ],
        }),
        deleteAnswer: build.mutation<void, Answer>({
            query: (answer) => ({ url: `/NI-api/answer/${answer.id}`, method: "DELETE" }),
            invalidatesTags: (result, error, arg) => ["Answer", { type: "Question", id: arg.questionId }],
        }),
        voteAnswer: build.mutation<void, { answer: Answer; vote: number }>({
            query: ({ answer, vote }) => ({
                url: `/NI-api/answer/${answer.id}/${vote > 0 ? "like" : "dislike"}`,
                method: "POST",
                data: { vote },
            }),
            async onQueryStarted({ answer, vote }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    answerApi.util.updateQueryData("getAnswers", answer.questionId, (answers) => {
                        const ans: Answer | undefined = answers.find((ans) => ans.id === answer.id);
                        if (ans) {
                            ans.votes -= ans.userVote;
                            if (vote === ans.userVote) {
                                ans.userVote = 0;
                            } else {
                                ans.votes += vote;
                                ans.userVote = vote;
                            }
                        }
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetAnswersQuery,
    useGetAnswerQuery,
    useCreateAnswerMutation,
    useUpdateAnswerMutation,
    useAcceptAnswerMutation,
    useDeleteAnswerMutation,
    useVoteAnswerMutation,
} = answerApi;
